/**
 * E-Mail-HTML → WhatsApp-taugliche PNG-Bilder (komplett im Browser, ohne Server).
 *
 * - Rendert das HTML in einem unsichtbaren iframe in Mailbreite (Standard 600 px) und
 *   fotografiert es mit html2canvas in doppelter Auflösung (scharf auf dem Handy).
 * - Sehr hohe Mails werden in mehrere Bilder geteilt – bevorzugt an einer einfarbigen
 *   Zeile (Abstand zwischen Abschnitten), damit kein Text zerschnitten wird.
 * - Links sind im Bild nicht klickbar → extrahiereLinks() liefert sie für den Begleittext.
 */
import html2canvas from 'html2canvas';

const warte = (ms) => new Promise((r) => setTimeout(r, ms));

/** Fragment (z. B. aus dem Editor) in ein vollständiges, schlichtes Dokument einbetten */
function alsDokument(html) {
  const s = String(html || '');
  if (/<html[\s>]/i.test(s)) return s;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{margin:0;padding:24px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#1f2937}
    img{max-width:100%;height:auto}
  </style></head><body>${s}</body></html>`;
}

async function ladeIframe(html, breite) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = `position:fixed;left:-20000px;top:0;width:${breite}px;height:200px;border:0;visibility:hidden;`;
  document.body.appendChild(iframe);
  await new Promise((resolve) => {
    iframe.onload = () => resolve();
    iframe.srcdoc = alsDokument(html);
  });
  const doc = iframe.contentDocument;
  // Bilder und Schriften abwarten (max. 5 s), sonst fehlen sie im PNG
  const bilder = Array.from(doc.images || []);
  await Promise.race([
    Promise.all(bilder.map((img) => (img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; })))),
    warte(5000),
  ]);
  try { await Promise.race([doc.fonts?.ready, warte(2000)]); } catch { /* ignore */ }
  return iframe;
}

/** Einfarbige Zeile in der Nähe von y suchen (nach oben), damit nicht mitten im Text geschnitten wird */
function findeSchnitt(ctx, breite, y, suchbereich) {
  for (let yy = y; yy > y - suchbereich && yy > 0; yy -= 2) {
    const zeile = ctx.getImageData(0, yy, breite, 1).data;
    const r0 = zeile[0], g0 = zeile[1], b0 = zeile[2];
    let einfarbig = true;
    for (let i = 0; i < zeile.length; i += 16) {
      if (Math.abs(zeile[i] - r0) > 8 || Math.abs(zeile[i + 1] - g0) > 8 || Math.abs(zeile[i + 2] - b0) > 8) { einfarbig = false; break; }
    }
    if (einfarbig) return yy;
  }
  return y;
}

const canvasZuBlob = (canvas) => new Promise((resolve, reject) =>
  canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG konnte nicht erzeugt werden'))), 'image/png'));

/**
 * @returns {Promise<Blob[]>} ein oder mehrere PNG-Bilder
 */
export async function htmlZuWhatsAppBildern(html, { breite = 600, skala = 2, maxHoehe = 1800 } = {}) {
  const iframe = await ladeIframe(html, breite);
  try {
    const doc = iframe.contentDocument;
    // Breitere Mails (z. B. 640 px) nicht abschneiden: Breite an den Inhalt anpassen (max. 900 px)
    const inhaltBreite = Math.min(Math.max(breite, doc.documentElement.scrollWidth, doc.body?.scrollWidth || 0), 900);
    iframe.style.width = `${inhaltBreite}px`;
    await warte(50);
    const hoehe = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0, 100);
    iframe.style.height = `${hoehe}px`;
    await warte(50);
    const canvas = await html2canvas(doc.documentElement, {
      backgroundColor: '#ffffff',
      scale: skala,
      useCORS: true,
      logging: false,
      width: inhaltBreite,
      height: hoehe,
      windowWidth: inhaltBreite,
      windowHeight: hoehe,
    });
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const segment = maxHoehe * skala;
    if (canvas.height <= segment * 1.15) return [await canvasZuBlob(canvas)];

    const blobs = [];
    let start = 0;
    while (start < canvas.height) {
      let ende = Math.min(start + segment, canvas.height);
      if (ende < canvas.height) ende = findeSchnitt(ctx, canvas.width, ende, 250 * skala);
      if (ende <= start + 100) ende = Math.min(start + segment, canvas.height);
      const teil = document.createElement('canvas');
      teil.width = canvas.width;
      teil.height = ende - start;
      teil.getContext('2d').drawImage(canvas, 0, start, canvas.width, ende - start, 0, 0, canvas.width, ende - start);
      blobs.push(await canvasZuBlob(teil));
      start = ende;
    }
    return blobs;
  } finally {
    iframe.remove();
  }
}

/** Links aus dem HTML für den Begleittext (Bild-Links sind nicht klickbar) */
export function extrahiereLinks(html) {
  try {
    const doc = new DOMParser().parseFromString(alsDokument(html), 'text/html');
    const gesehen = new Set();
    const out = [];
    doc.querySelectorAll('a[href]').forEach((a) => {
      const href = (a.getAttribute('href') || '').trim();
      if (!/^(https?:|mailto:|tel:)/i.test(href) || gesehen.has(href)) return;
      gesehen.add(href);
      const text = (a.textContent || '').replace(/\s+/g, ' ').replace(/^[→›»\s]+/, '').trim();
      const ziel = href.replace(/^mailto:/i, '').replace(/^tel:/i, '');
      out.push({ href, text: text && text !== ziel ? text : '', ziel });
    });
    return out;
  } catch {
    return [];
  }
}

export function begleittextVorschlag(betreff, links) {
  const zeilen = [];
  if (betreff) zeilen.push(betreff);
  if (links.length) {
    if (zeilen.length) zeilen.push('');
    links.forEach((l) => zeilen.push(l.text ? `${l.text}: ${l.ziel}` : l.ziel));
  }
  return zeilen.join('\n');
}

/** Telefonnummer → internationale Ziffernfolge für wa.me (ohne +, 0 → 49) */
export function whatsAppNummer(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  let num = s.replace(/\D/g, '');
  if (s.startsWith('+')) num = s.slice(1).replace(/\D/g, '');
  else if (num.startsWith('00')) num = num.slice(2);
  else if (num.startsWith('0')) num = '49' + num.slice(1);
  return num;
}

// Dateinamen ohne Umlaute (Projektregel; manche Browser verwerfen sonst den Namen)
const ohneUmlaute = (t) => t.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/Ä/g, 'Ae')
  .replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue').replace(/ß/g, 'ss').replace(/[–—]/g, '-').replace(/[^\x20-\x7E]/g, '');

export const dateiname = (betreff, i, n) => {
  const basis = ohneUmlaute(String(betreff || 'Nachricht')).replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Nachricht';
  return n > 1 ? `${basis} (${i + 1} von ${n}).png` : `${basis}.png`;
};
